// Printed wayfinding signs — the shapes and rules shared by the Building
// Directories page, its print sheet and the admin scan report.
//
// THE PRINTED QR CARRIES THE NAV NODE ID, with the sign's identifier alongside:
//
//     https://myumaps.ca/umanitoba?n=id_1784566572754_26&s=E1-F1A
//
// `n` is the whole payload as far as routing is concerned — the pathfinder
// resolves it straight out of the graph it has already loaded, so a sign works
// the moment it is printed and nothing has to be published first. That is the
// point of the format: the earlier `?qr=<code>` form was a lookup into a file
// that had to be deployed before a freshly printed sign would scan, which put a
// release between placing a sign and it working.
//
// `s` is the human identifier, and exists so the app can say "From E1-F1A"
// rather than "From id_1784566572754_26". Display only — never trusted for
// routing, so a mangled or missing `s` costs a label, not a route.
//
// The honest cost: re-pointing a sign at a different node, or retiring one,
// means reprinting it. `enabled` therefore controls what goes on a SHEET, not
// whether a sign already on a wall still scans.
//
// Kept in step by hand with UMapsTools' src/utils/qrCodes.js, which authors the
// same records for the internal tool. The two must agree on `qrUrl` exactly.

/** The public base a printed code points at. */
export const DEFAULT_BASE = 'https://myumaps.ca/umanitoba';

/** The URL a printed sign carries. One definition, because the encoder, the
 *  print sheet and the pathfinder all have to agree on it exactly. */
export function signUrl(sign, base = DEFAULT_BASE) {
  const b = String(base || DEFAULT_BASE).replace(/\?.*$/, '').replace(/\/+$/, '');
  if (!sign?.nodeId) return b;
  const q = `n=${encodeURIComponent(sign.nodeId)}`;
  return sign.code ? `${b}?${q}&s=${encodeURIComponent(sign.code)}` : `${b}?${q}`;
}

/** Identifiers are read off signage and typed into reports, so keep them
 *  unambiguous: upper case, alphanumerics and hyphens only. */
export function normalizeCode(raw) {
  return String(raw || '')
    .toUpperCase()
    .replace(/[^A-Z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 24);
}

export const floorTag = f => (f === -1 ? 'T' : f === 0 ? 'O' : String(f));

export const prettifySlug = slug => String(slug || '')
  .split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/**
 * A suggested identifier for a new sign: building initials + floor + a running
 * letter, e.g. EITC E1 floor 2 → "E1-F2A".
 *
 * Uniqueness is checked against every sign passed in, not just the building's —
 * the code is what a scan report groups by campus-wide, and the server refuses
 * a collision, so proposing one here would only produce a save that fails.
 */
export function suggestCode(slug, floor, existing = []) {
  const initials = String(slug || '')
    .split('_')
    .map(w => w.replace(/[^a-z0-9]/gi, ''))
    .filter(Boolean)
    .map(w => (/^\d/.test(w) ? w : w[0]))
    .join('')
    .toUpperCase()
    .slice(0, 4) || 'QR';
  const f = floor === -1 ? 'T' : floor === 0 ? 'O' : `F${floor}`;
  const taken = new Set(existing.map(e => e.code));
  for (let i = 0; i < 260; i++) {
    const letter = i < 26
      ? String.fromCharCode(65 + i)
      : String.fromCharCode(64 + Math.floor(i / 26)) + String.fromCharCode(65 + (i % 26));
    const code = normalizeCode(`${initials}-${f}${letter}`);
    if (!taken.has(code)) return code;
  }
  return normalizeCode(`${initials}-${f}-${Date.now().toString(36).slice(-4)}`);
}

/** A new sign record. `scans` is never written here — the site owns that
 *  counter, and the server preserves it across saves. */
export function makeSign({ slug, floor, nodeId, code, label = '', location = '' }) {
  return {
    id: `qr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    code,
    building: slug,
    floor,
    nodeId,
    label,
    location,
    enabled: true,
    createdAt: new Date().toISOString(),
    notes: '',
  };
}

/** A readable name for a nav node, for the sign's default label. */
export function nodeName(n) {
  if (!n) return '';
  return n.label
    || (n.room_number ? `Room ${n.room_number}` : '')
    || `${n.type || 'waypoint'} ${String(n.id).slice(-4)}`;
}
